import { isEmpty, sortBy } from 'lodash-es';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router';
import { useThrottledCallback } from 'use-debounce';
import { useDebounceCallback } from 'usehooks-ts';
import { VList, type VListHandle } from 'virtua';
import MessageListItem from '@/components/conversations/conversation-list/message-list-item/message-list-item';
import EmptyLayout from '@/components/layouts/empty-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useMarkSessionRead from '@/hooks/conversations/use-mark-session-read';
import useMessageHistory from '@/hooks/conversations/use-message-history';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useAdjustLayout from '@/hooks/use-adjust-layout';
import useDocumentVisible from '@/hooks/use-document-visible';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { groupMessagesByMinutes } from '@/utils/group-messages';
import { generateSessionKey } from '@/utils/session';
import DateHeader from '../conversation-virtuoso-list/date-header';
import ScrollToEndButton from './scroll-to-end-button';
import ScrollToLastReadButton from './scroll-to-last-read-button';

interface ConversationListProps {
  initialUnreadCount?: number;
  searchId?: number;
  lastMsgID: number;
  lastReadId?: number;
  isFromUnread?: boolean;
}

export default function ConversationVirtuaList({
  initialUnreadCount = 0,
  searchId,
  lastMsgID,
  lastReadId,
  isFromUnread,
}: ConversationListProps) {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { isKeyboardVisible } = useAdjustLayout({ inputTargetSelector: '#quill-editor .ql-editor' });

  const vListRef = useRef<VListHandle>(null);
  const prevGroupedMessagesLength = useRef<number | null>(null);
  const startIndex = useRef<number | null>(null);
  const positionRef = useRef<number>(0);
  const hasLastPositionRef = useRef<boolean>(false);

  const [atEdge, setAtEdge] = useState<'head' | 'tail' | null>('tail');
  const debouncedAtEdge = useDeferredValue(atEdge);
  const [isScrolling, setIsScrolling] = useState(false);
  const [unreadCount, setUnreadCount] = useState(initialUnreadCount);

  const { type, id } = useConversationStore();
  const sessionKey = generateSessionKey(type, id);
  const { setLastPosition, lastPositions } = useConversationSessionStore();

  const lastPosition = (
    typeof lastPositions[sessionKey] === 'number'
      ? lastPositions[sessionKey]
      : lastPositions[sessionKey]?.[searchId || 'default']
  ) as number;

  const { canManage } = useGroupInfo(type === 'group' ? id : null);
  const { isHighlighted, messages: messagesMap } = useMessageStore();
  const messages = useMemo(() => {
    const list = Array.from(messagesMap.values());
    return sortBy(
      list.filter(({ MsgType }) => !(MsgType === ConversationMessageContentType.LeaveGroup && !canManage)),
      'ID',
    );
  }, [messagesMap, canManage]);

  const groupedMessages = useMemo(() => groupMessagesByMinutes(messages), [messages]);
  const groupLengthRef = useRef<number>(groupedMessages.length - 1);

  const isDocumentVisible = useDocumentVisible();
  const { markAllRead } = useMarkSessionRead();

  const {
    fetchPreviousPage,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    isEnabled: fetchEnabled,
    setEnable: setEnableFetch,
    setMessageId,
    isLoading: isFetchingHistory,
  } = useMessageHistory(type, id, searchId, !searchId);

  const canScroll = () => {
    if (!vListRef.current) return false;
    return vListRef.current.scrollSize > vListRef.current.viewportSize;
  };

  const isAtTail = () => {
    if (!vListRef.current) return false;
    return vListRef.current.scrollOffset - vListRef.current.scrollSize + vListRef.current.viewportSize >= -1.5;
  };

  const scrollToIndex = (index: number, align: 'start' | 'end' | 'center' = 'start', smooth = false) => {
    if (!vListRef.current) return;
    vListRef.current.scrollToIndex(index, { align, smooth });
  };

  const scrollToMessageId = (id: number) => {
    if (id < 0) return;
    const index = groupedMessages.findIndex((message) => 'ID' in message && message.ID === id);
    scrollToIndex(index, 'start');
  };

  const scrollToTail = () => scrollToIndex(groupLengthRef.current, 'end');

  const debouncedScrollToTail = useDebounceCallback(scrollToTail, 200);

  const adjustScrollAfterPrepend = () => {
    if (atEdge === 'tail') return;

    const prevLen = prevGroupedMessagesLength.current;
    const currentLen = groupedMessages.length;
    const startIdx = startIndex.current;

    if (prevLen === null || startIdx === null) return;
    if (currentLen <= prevLen) return;

    const prependedCount = currentLen - prevLen;
    const newTopIndex = prependedCount + startIdx + 1;

    prevGroupedMessagesLength.current = null;
    startIndex.current = null;

    scrollToIndex(newTopIndex, 'start');
  };

  const handleMarkAllRead = () => {
    if (canScroll() && atEdge !== 'tail') return;
    if (!isDocumentVisible) return;
    markAllRead(type, id, lastMsgID, lastReadId ?? 0, 'virtual-list');
  };

  const handleOnScroll = (offset: number) => {
    if (!vListRef.current) return;
    positionRef.current = offset;
    const isAtHeadEdge = offset < 5;
    const isAtTailEdge = isAtTail();
    setAtEdge(isAtTailEdge ? 'tail' : isAtHeadEdge ? 'head' : null);
    setIsScrolling(true);
  };

  const handleOnScrollEnd = () => {
    handleLoadNextPage();
    handleLoadPreviousPage();
    handleMarkAllRead();
    setIsScrolling(false);
  };

  const handleLoadNextPage = () => {
    if (!vListRef.current) return;
    const isAtTailEdge =
      vListRef.current.scrollOffset - vListRef.current.scrollSize + vListRef.current.viewportSize >= -1.5;
    if (isAtTailEdge && !isFetchingNextPage && hasNextPage) {
      ThrottledLoadNextPage();
    }
  };

  const handleLoadPreviousPage = () => {
    if (!vListRef.current) return;
    const isAtHeadEdge = vListRef.current.scrollOffset < 50;
    if (isAtHeadEdge && !isFetchingPreviousPage && hasPreviousPage) {
      prevGroupedMessagesLength.current = groupedMessages.length;
      startIndex.current = vListRef.current?.findStartIndex();
      ThrottledStartReached();
    }
  };

  const ThrottledLoadNextPage = useThrottledCallback(async () => {
    if (!fetchEnabled || !hasNextPage || isFetchingNextPage) return;
    await fetchNextPage();
  }, 600);

  const ThrottledStartReached = useThrottledCallback(async () => {
    if (!fetchEnabled || !hasPreviousPage || isFetchingPreviousPage) return;
    await fetchPreviousPage();
  }, 600);

  const navigateToUnreadMessage = () => {
    // NOTE: 點未讀訊息時，使用 searchId 搜尋的方式定位到最後一則未讀訊息。
    navigate(
      type === 'session'
        ? ROUTES.MESSAGES.SESSIONS.$buildPath({
            params: { id },
            searchParams: { search: lastReadId, fromUnread: 1 },
          })
        : ROUTES.MESSAGES.GROUPS.$buildPath({
            params: { id },
            searchParams: { search: lastReadId, fromUnread: 1 },
          }),
      { replace: true },
    );
  };

  const handleSearchModeScrollToEnd = async () => {
    navigate(
      type === 'session'
        ? ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id } })
        : ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id } }),
      { replace: true },
    );
  };

  useEffect(() => {
    if (isDesktop) return;
    // 從聊天室的子路由返回時，才回復卷軸位置
    if (lastPosition !== undefined && navigationType === 'POP') hasLastPositionRef.current = true;
    vListRef.current?.scrollTo(lastPosition || 0);
    return () => setLastPosition(sessionKey, positionRef.current, searchId);
  }, []);

  useEffect(() => {
    // NOTE: 當聊天室訊息不足以出現卷軸時，需要將每則訊息都標記為已讀
    if (!canScroll()) handleMarkAllRead();
  }, [lastMsgID]);

  useEffect(() => {
    const setTimeoutId = setTimeout(() => {
      if (atEdge === 'tail' && !hasLastPositionRef.current) scrollToTail();
      if (!isKeyboardVisible && !canScroll()) setAtEdge('tail');
    }, 200);
    return () => clearTimeout(setTimeoutId);
  }, [isKeyboardVisible]);

  useEffect(() => {
    setUnreadCount(initialUnreadCount);
  }, [initialUnreadCount]);

  useEffect(() => {
    if (!isFetchingHistory && searchId) {
      const timeoutId = setTimeout(() => {
        if (searchId && !hasLastPositionRef.current) {
          scrollToMessageId(searchId);
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isFetchingHistory, searchId]);

  useEffect(() => {
    setEnableFetch(false);

    setMessageId(searchId);

    const timeoutId = setTimeout(() => {
      setEnableFetch(true);
    }, 2_000);

    return () => clearTimeout(timeoutId);
  }, [searchId]);

  useEffect(() => {
    groupLengthRef.current = groupedMessages.length - 1;

    adjustScrollAfterPrepend();

    // NOTE: 搜尋進來時，如果還有下頁，則不置底
    if (searchId && hasNextPage) return;

    const lastMessage = messages.at(-1);

    if ((atEdge === 'tail' && !hasLastPositionRef.current) || lastMessage?.Status === null) {
      scrollToTail();
      // NOTE: 初次載入聊天室 DOM 可能尚未渲染完成，導致置底失敗，暫時使用 debouncedScrollToTail 補足剩下置底的距離
      debouncedScrollToTail();
    }
  }, [groupedMessages]);

  useEffect(() => {
    if (navigationType === 'POP') {
      useMessageStore.setState((s) => ({ ...s, isHighlighted: true }));
    }
  }, [navigationType]);

  return (
    <>
      {!isEmpty(groupedMessages) ? (
        <VList
          className='bg-base-100 flex-1 overscroll-y-none'
          ref={vListRef}
          overscan={6}
          onScroll={handleOnScroll}
          onScrollEnd={handleOnScrollEnd}>
          {groupedMessages.map((item) => {
            if ('isDate' in item) {
              return (
                <DateHeader
                  key={item.key}
                  date={item.timestamp}
                />
              );
            }

            return (
              <MessageListItem
                key={item.ID}
                type='clamped'
                isHighlighted={searchId === item.ID && !isFromUnread && !isHighlighted}
                message={item}
              />
            );
          })}
        </VList>
      ) : isFetchingHistory ? (
        <LoadingLayout
          className='bg-base-100'
          promise={true}
        />
      ) : (
        <EmptyLayout
          label={type === 'group' ? 'conversation.group.welcome' : 'conversation.message.start-messaging'}
          className='flex-1 bg-base-100'
        />
      )}

      <ScrollToLastReadButton
        count={unreadCount}
        onClick={navigateToUnreadMessage}
      />

      {canScroll() && (
        <ScrollToEndButton
          isAtTailEdge={debouncedAtEdge === 'tail'}
          isScrolling={isScrolling}
          onClick={handleSearchModeScrollToEnd}
        />
      )}
    </>
  );
}
